import { type VariantProps, cva } from 'class-variance-authority';
import React from 'react';

import { cn } from '@/lib/cn';

import { TextVariant } from './Text.variant';
import { textVariantClassMap } from './textVariantClassMap';

type TextAsType = 'p' | 'span' | 'h1' | 'h2' | 'h3' | 'h4';

const textStyles = cva('', {
  variants: {
    variant: {
      [TextVariant.h1]: `text-h1 ${textVariantClassMap[TextVariant.h1]}`,
      [TextVariant.h2]: `text-h2 ${textVariantClassMap[TextVariant.h2]}`,
      [TextVariant.h3]: `text-h3 ${textVariantClassMap[TextVariant.h3]}`,
      [TextVariant.h4]: `text-h4 ${textVariantClassMap[TextVariant.h4]}`,
      [TextVariant.CardHeader]: `text-CardHeader ${textVariantClassMap[TextVariant.CardHeader]}`,
      [TextVariant.p]: `text-p ${textVariantClassMap[TextVariant.p]}`,
      [TextVariant.pBold]: `text-pBold ${textVariantClassMap[TextVariant.pBold]}`,
      [TextVariant.pSmall]: `text-pSmall ${textVariantClassMap[TextVariant.pSmall]}`,
      [TextVariant.list]: `text-list ${textVariantClassMap[TextVariant.list]}`,
      [TextVariant.inlineCode]: `text-inlineCode ${textVariantClassMap[TextVariant.inlineCode]}`,
      [TextVariant.buttonLabel]: `text-buttonLabel ${textVariantClassMap[TextVariant.buttonLabel]}`,
      [TextVariant.buttonLabelSmall]: `text-buttonLabelSmall ${textVariantClassMap[TextVariant.buttonLabelSmall]}`,
      [TextVariant.badgeLabel]: `text-badgeLabel ${textVariantClassMap[TextVariant.badgeLabel]}`,
      [TextVariant.labelLarge]: `text-labelLarge ${textVariantClassMap[TextVariant.labelLarge]}`,
      [TextVariant.labelSemiLarge]: `text-labelSemiLarge ${textVariantClassMap[TextVariant.labelSemiLarge]}`,
      [TextVariant.label]: `text-label ${textVariantClassMap[TextVariant.label]}`,
      [TextVariant.labelReg]: `text-labelReg ${textVariantClassMap[TextVariant.labelReg]}`,
      [TextVariant.labelSmall]: `text-labelSmall ${textVariantClassMap[TextVariant.labelSmall]}`,
      [TextVariant.labelSmallReg]: `text-labelSmallReg ${textVariantClassMap[TextVariant.labelSmallReg]}`,
      [TextVariant.labelExSmall]: `text-labelExSmall ${textVariantClassMap[TextVariant.labelExSmall]}`,
      [TextVariant.inputLabelLarge]: `text-inputLabelLarge ${textVariantClassMap[TextVariant.inputLabelLarge]}`,
      [TextVariant.inputLabel]: `text-inputLabel ${textVariantClassMap[TextVariant.inputLabel]}`,
      [TextVariant.numberSemiLarge]: `text-numberSemiLarge ${textVariantClassMap[TextVariant.numberSemiLarge]}`,
      /**
       * 以下はFigmaのデザインデータにないがデザイントークンに存在するので定義
       * TODO:中沢さんに確認して修正・追加をしていく
       * https://www.figma.com/design/xOnr21NAsSbNuEglbEeZog/901_Components?node-id=1318-7964&m=dev
       */
      [TextVariant.blockquote]: `text-blockquote ${textVariantClassMap[TextVariant.blockquote]}`,
      [TextVariant.tableHead]: `text-tableHead ${textVariantClassMap[TextVariant.tableHead]}`,
      [TextVariant.tableItem]: `text-tableItem ${textVariantClassMap[TextVariant.tableItem]}`,
      [TextVariant.tableItemNumber]: `text-tableItemNumber ${textVariantClassMap[TextVariant.tableItemNumber]}`,
      [TextVariant.numberLarge]: `text-numberLarge ${textVariantClassMap[TextVariant.numberLarge]}`,
      [TextVariant.secret]: `text-secret ${textVariantClassMap[TextVariant.secret]}`,
    },
    /**
     * テキストを折り返す
     * @see https://tailwindcss.com/docs/word-break#breaking-words
     */
    breakWord: {
      true: 'wrap-break-word break-all',
    },
    /**
     * テキストを省略する
     * @see https://tailwindcss.com/docs/text-overflow#truncating-text
     */
    truncate: {
      true: 'truncate',
    },
    /**
     * テキストを非表示にする
     * @see https://tailwindcss.com/docs/screen-readers#hiding-elements
     */
    visuallyHidden: {
      true: 'sr-only',
    },
    /**
     * テキストを打ち消す
     * @see https://tailwindcss.com/docs/text-decoration#line-through
     */
    lineThrough: {
      true: 'line-through',
    },
    /**
     * 数値を等幅にする
     * @see https://tailwindcss.com/docs/font-variant-numeric#using-tabular-figures
     */
    numeric: {
      true: 'tabular-nums',
    },
  },
});

// cvaのVariantPropsを拡張したPropの型定義
export interface TextProps
  extends React.PropsWithChildren, VariantProps<typeof textStyles> {
  /**
   * 要素のタイプ
   */
  as?: TextAsType;
  /**
   * HTMLのid属性
   */
  id?: string;
  /**
   * 追加のクラス名
   */
  className?: string;
  /**
   * 使用するタイプスタイル
   */
  variant?: TextVariant;
}

/**
 * `dresscode-ui`が管理するデザイントークンを適用したテキストコンポーネント。
 *
 * font-size,font-weight, line-height をvariantで指定することができます。(デフォルトは`p`スタイル)
 *
 * text-color, text-decoration, text-align は現時点ではclassNameを指定することを想定しています。
 */
export const Text = React.memo(function Text({
  children,
  variant = TextVariant.p,
  as,
  id,
  numeric,
  breakWord,
  truncate,
  visuallyHidden,
  lineThrough,
  className,
}: TextProps) {
  // デフォルトのHTML要素をvariantに基づいて設定
  const Element = as || getDefaultElementFromVariant(variant);

  const classes = cn(
    // cvaでスタイルを生成
    textStyles({
      variant,
      breakWord,
      truncate,
      visuallyHidden,
      lineThrough,
      numeric,
    }),
    className,
  );

  return (
    <Element id={id} className={classes}>
      {children}
    </Element>
  );
});

// variantからデフォルトのHTML要素を決定する関数
function getDefaultElementFromVariant(variant: TextVariant): TextAsType {
  switch (variant) {
    case TextVariant.h1:
      return 'h1';
    case TextVariant.h2:
      return 'h2';
    case TextVariant.h3:
      return 'h3';
    case TextVariant.h4:
      return 'h4';
    case TextVariant.CardHeader:
      return 'h3';
    case TextVariant.p:
    case TextVariant.pSmall:
      return 'p';
    case TextVariant.list:
    case TextVariant.blockquote:
    case TextVariant.tableHead:
    case TextVariant.tableItem:
    case TextVariant.tableItemNumber:
    case TextVariant.inlineCode:
    case TextVariant.buttonLabel:
    case TextVariant.buttonLabelSmall:
    case TextVariant.badgeLabel:
    case TextVariant.labelLarge:
    case TextVariant.labelSemiLarge:
    case TextVariant.label:
    case TextVariant.labelReg:
    case TextVariant.labelSmall:
    case TextVariant.labelSmallReg:
    case TextVariant.labelExSmall:
    case TextVariant.inputLabelLarge:
    case TextVariant.inputLabel:
    case TextVariant.numberSemiLarge:
    case TextVariant.numberLarge:
    case TextVariant.secret:
      return 'span';
    default:
      return 'p';
  }
}
